/**
 * Core RTDB Cache Service
 * Handles all cache operations with Firebase Realtime Database
 */

import { ref, get, set, onValue, off, runTransaction, serverTimestamp } from 'firebase/database';
import {
  rtdb,
  CACHE_PATHS,
  getCacheKey,
  isCacheExpired,
  createCacheEntry,
  logCache,
  trackCacheMetric,
  RTDB_FEATURES
} from './rtdbConfig';
import requestDeduplicator from './requestDeduplicator';
import rateLimiter from './rateLimiter';

class RTDBCacheService {
  constructor() {
    this.localCache = new Map(); // In-memory cache for faster access
    this.subscribers = new Map(); // Active subscriptions
    this.connectionStatus = false;
    this.performanceMetrics = {
      cacheHits: 0,
      cacheMisses: 0,
      fetchErrors: 0,
      avgResponseTime: 0,
      totalRequests: 0
    };
    this.maxCacheSize = 1000; // Maximum entries in local cache
    this.lastCleanup = Date.now();
    this.cleanupInterval = 5 * 60 * 1000; // Cleanup every 5 minutes

    // Monitor connection status
    this.initConnectionMonitoring();

    // Start periodic cleanup
    this.startPeriodicCleanup();
  }

  /**
   * Initialize connection monitoring
   */
  initConnectionMonitoring() {
    try {
      const connectedRef = ref(rtdb, '.info/connected');
      onValue(connectedRef, (snapshot) => {
        const wasConnected = this.connectionStatus;
        this.connectionStatus = snapshot.val() === true;

        // Log connection state changes
        if (wasConnected !== this.connectionStatus) {
          logCache('info', 'connection', {
            connected: this.connectionStatus,
            subscriberCount: this.subscribers.size,
            cacheSize: this.localCache.size
          });
        }
      });
    } catch (error) {
      console.warn('Failed to monitor RTDB connection:', error.message);
      this.connectionStatus = false;
    }
  }

  /**
   * Start periodic cleanup of expired cache entries
   */
  startPeriodicCleanup() {
    setInterval(() => {
      this.cleanupExpiredEntries();
    }, this.cleanupInterval);
  }

  /**
   * Clean up expired entries from local cache
   */
  cleanupExpiredEntries() {
    const now = Date.now();
    let cleaned = 0;

    // Clean expired entries
    for (const [key, value] of this.localCache.entries()) {
      if (isCacheExpired(value)) {
        this.localCache.delete(key);
        cleaned++;
      }
    }

    // If cache is too large, remove oldest entries
    if (this.localCache.size > this.maxCacheSize) {
      const entriesToRemove = this.localCache.size - this.maxCacheSize;
      const entries = Array.from(this.localCache.entries());

      // Sort by last updated time (oldest first)
      entries.sort((a, b) => (a[1].lastUpdated || 0) - (b[1].lastUpdated || 0));

      // Remove oldest entries
      for (let i = 0; i < entriesToRemove; i++) {
        this.localCache.delete(entries[i][0]);
        cleaned++;
      }
    }

    if (cleaned > 0) {
      logCache('info', 'cleanup', {
        cleaned,
        remaining: this.localCache.size,
        elapsed: Date.now() - now
      });
    }

    this.lastCleanup = now;
  }

  /**
   * Track performance metrics
   */
  trackPerformance(startTime, success, source = 'unknown') {
    const duration = Date.now() - startTime;
    this.performanceMetrics.totalRequests++;

    if (success) {
      if (source === 'memory' || source === 'rtdb') {
        this.performanceMetrics.cacheHits++;
      } else {
        this.performanceMetrics.cacheMisses++;
      }
    } else {
      this.performanceMetrics.fetchErrors++;
    }

    // Update rolling average response time
    const weight = 0.1; // Weight for exponential moving average
    this.performanceMetrics.avgResponseTime =
      this.performanceMetrics.avgResponseTime * (1 - weight) + duration * weight;

    // Log performance warning if response is slow
    if (duration > 3000) {
      logCache('info', 'slow-response', {
        duration,
        source,
        avgResponseTime: Math.round(this.performanceMetrics.avgResponseTime)
      });
    }
  }

  /**
   * Get data from cache with improved error handling and fallback
   * @param {string} path - Cache path
   * @param {Function} fetchFn - Function to fetch fresh data if cache miss
   * @param {number} ttlMs - TTL in milliseconds
   * @param {string} userId - Optional user ID for rate limiting
   * @returns {Promise<any>} Cached or fresh data
   */
  async get(path, fetchFn, ttlMs, userId = 'anonymous') {
    if (!RTDB_FEATURES.USE_RTDB_CACHE) {
      return fetchFn();
    }

    const startTime = Date.now();
    let rtdbAttempted = false;
    let source = 'direct';

    // Check rate limits first
    const rateLimitCheck = await rateLimiter.shouldAllowRequest(path, userId);
    if (!rateLimitCheck.allowed) {
      logCache('error', `Rate limit exceeded: ${path}`, rateLimitCheck);
      throw new Error(`Rate limit exceeded: ${rateLimitCheck.reason}. Retry after: ${new Date(rateLimitCheck.retryAfter).toISOString()}`);
    }

    try {
      // 1. Check local memory cache first (fastest)
      const localCached = this.localCache.get(path);
      if (localCached && !isCacheExpired(localCached)) {
        logCache('hit', path, { source: 'memory' });
        // Fire and forget metric tracking
        trackCacheMetric('hits').catch(() => {});
        this.trackPerformance(startTime, true, 'memory');
        return localCached.data;
      }

      // 2. Check RTDB cache (with timeout for reliability)
      if (this.connectionStatus) {
        rtdbAttempted = true;
        const cacheRef = ref(rtdb, path);

        // Add timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('RTDB timeout')), 5000)
        );

        const snapshot = await Promise.race([get(cacheRef), timeoutPromise]);

        if (snapshot.exists()) {
          const cachedData = snapshot.val();

          if (!isCacheExpired(cachedData)) {
            // Update local cache
            this.localCache.set(path, cachedData);

            // Increment hit counter (fire and forget)
            this.incrementHits(path).catch(() => {});

            logCache('hit', path, { source: 'rtdb', ttl: cachedData.ttl });
            trackCacheMetric('hits').catch(() => {});
            this.trackPerformance(startTime, true, 'rtdb');
            return cachedData.data;
          } else {
            logCache('expired', path, { ttl: cachedData.ttl });
          }
        } else {
          logCache('miss', path);
        }
      } else {
        logCache('info', 'RTDB offline, using direct fetch', { path });
      }

      // 3. Cache miss or expired - fetch fresh data with deduplication
      trackCacheMetric('misses').catch(() => {});

      // Use request deduplicator to prevent multiple simultaneous fetches
      const freshData = await requestDeduplicator.execute(path, fetchFn);

      // 4. Update cache (fire and forget to not block response)
      this.set(path, freshData, ttlMs).catch(err => {
        logCache('error', 'Failed to update cache after fetch', { error: err.message });
      });

      this.trackPerformance(startTime, true, source);
      return freshData;
    } catch (error) {
      // Log error only if RTDB was attempted
      if (rtdbAttempted) {
        logCache('error', `RTDB error, falling back to direct fetch: ${path}`, { error: error.message });
      }

      // Always fallback to direct fetch on any error
      try {
        const result = await fetchFn();
        this.trackPerformance(startTime, true, 'fallback');
        return result;
      } catch (fetchError) {
        logCache('error', `Both RTDB and direct fetch failed: ${path}`, { error: fetchError.message });
        this.trackPerformance(startTime, false, 'error');
        throw fetchError;
      }
    }
  }

  /**
   * Set data in cache
   * @param {string} path - Cache path
   * @param {any} data - Data to cache
   * @param {number} ttlMs - TTL in milliseconds
   */
  async set(path, data, ttlMs) {
    if (!RTDB_FEATURES.USE_RTDB_CACHE) return;

    try {
      const cacheEntry = createCacheEntry(data, ttlMs);
      
      // Update local cache immediately
      this.localCache.set(path, cacheEntry);
      
      // Update RTDB
      const cacheRef = ref(rtdb, path);
      await set(cacheRef, {
        ...cacheEntry,
        timestamp: serverTimestamp()
      });
      
      logCache('set', path, { ttl: cacheEntry.ttl });
      trackCacheMetric('writes');
    } catch (error) {
      logCache('error', `Failed to set cache: ${path}`, { error: error.message });
    }
  }

  /**
   * Subscribe to real-time updates with error handling
   * @param {string} path - Cache path
   * @param {Function} callback - Callback for updates
   * @returns {Function} Unsubscribe function
   */
  subscribe(path, callback) {
    if (!RTDB_FEATURES.USE_RTDB_CACHE) {
      return () => {}; // No-op unsubscribe
    }

    const cacheRef = ref(rtdb, path);

    const listener = onValue(cacheRef,
      (snapshot) => {
        try {
          if (snapshot.exists()) {
            const cachedData = snapshot.val();

            if (!isCacheExpired(cachedData)) {
              // Update local cache
              this.localCache.set(path, cachedData);
              callback(cachedData.data);
            } else {
              callback(null); // Trigger refresh
            }
          } else {
            callback(null); // No data
          }
        } catch (error) {
          logCache('error', `Subscription callback error: ${path}`, { error: error.message });
          callback(null); // Trigger refresh on error
        }
      },
      (error) => {
        // Error callback for subscription failures
        logCache('error', `Subscription failed: ${path}`, { error: error.message });
        callback(null); // Trigger refresh on subscription error
      }
    );

    // Store subscription
    this.subscribers.set(path, listener);

    // Return unsubscribe function
    return () => {
      try {
        off(cacheRef, 'value', listener);
        this.subscribers.delete(path);
      } catch (error) {
        logCache('error', `Unsubscribe failed: ${path}`, { error: error.message });
      }
    };
  }

  /**
   * Increment hit counter for analytics (non-blocking)
   * @param {string} path - Cache path
   */
  async incrementHits(path) {
    if (!RTDB_FEATURES.ENABLE_ANALYTICS) return;

    try {
      const hitsRef = ref(rtdb, `${path}/hits`);
      await runTransaction(hitsRef, (current) => {
        return (current || 0) + 1;
      }, {
        applyLocally: false  // Don't apply locally, wait for server
      });
    } catch (error) {
      // Silent fail for analytics - it's non-critical
      if (RTDB_FEATURES.ENABLE_DEBUG) {
        console.debug('Hit counter update failed (non-critical):', path);
      }
    }
  }

  /**
   * Clear specific cache entry
   * @param {string} path - Cache path
   */
  async clear(path) {
    try {
      this.localCache.delete(path);
      const cacheRef = ref(rtdb, path);
      await set(cacheRef, null);
      logCache('info', `Cleared cache: ${path}`);
    } catch (error) {
      logCache('error', `Failed to clear cache: ${path}`, { error: error.message });
    }
  }

  /**
   * Clear all cache
   */
  async clearAll() {
    try {
      this.localCache.clear();
      const cacheRef = ref(rtdb, 'cache');
      await set(cacheRef, null);
      logCache('info', 'Cleared all cache');
    } catch (error) {
      logCache('error', 'Failed to clear all cache', { error: error.message });
    }
  }

  /**
   * Get cache statistics
   */
  async getStats() {
    try {
      const statsRef = ref(rtdb, `${CACHE_PATHS.METADATA}/stats`);
      const snapshot = await get(statsRef);
      
      if (snapshot.exists()) {
        return snapshot.val();
      }
      
      return {
        hits: 0,
        misses: 0,
        writes: 0,
        errors: 0
      };
    } catch (error) {
      logCache('error', 'Failed to get stats', { error: error.message });
      return null;
    }
  }

  /**
   * Check if connected to RTDB
   */
  isConnected() {
    return this.connectionStatus;
  }

  /**
   * Get cache size info with performance metrics
   */
  getCacheInfo() {
    const hitRate = this.performanceMetrics.totalRequests > 0
      ? (this.performanceMetrics.cacheHits / this.performanceMetrics.totalRequests * 100).toFixed(2)
      : 0;

    return {
      memoryCacheSize: this.localCache.size,
      maxCacheSize: this.maxCacheSize,
      activeSubscriptions: this.subscribers.size,
      connected: this.connectionStatus,
      performance: {
        hitRate: `${hitRate}%`,
        avgResponseTime: Math.round(this.performanceMetrics.avgResponseTime) + 'ms',
        totalRequests: this.performanceMetrics.totalRequests,
        errors: this.performanceMetrics.fetchErrors,
        lastCleanup: new Date(this.lastCleanup).toLocaleTimeString()
      }
    };
  }

  /**
   * Health check for monitoring
   */
  async healthCheck() {
    const startTime = Date.now();

    try {
      // Test RTDB connection
      if (this.connectionStatus) {
        const testRef = ref(rtdb, '.info/serverTimeOffset');
        await get(testRef);
      }

      const info = this.getCacheInfo();
      const elapsed = Date.now() - startTime;

      return {
        status: 'healthy',
        responseTime: elapsed,
        ...info
      };
    } catch (error) {
      return {
        status: 'degraded',
        error: error.message,
        responseTime: Date.now() - startTime,
        ...this.getCacheInfo()
      };
    }
  }
}

// Create singleton instance
const rtdbCache = new RTDBCacheService();

// Export for browser console debugging
if (typeof window !== 'undefined' && RTDB_FEATURES.ENABLE_DEBUG) {
  window.RTDBCache = {
    // Basic operations
    get: (path) => rtdbCache.localCache.get(path),
    clear: (path) => rtdbCache.clear(path),
    clearAll: () => rtdbCache.clearAll(),

    // Monitoring
    stats: () => rtdbCache.getStats(),
    info: () => rtdbCache.getCacheInfo(),
    health: () => rtdbCache.healthCheck(),

    // Performance
    performance: () => rtdbCache.performanceMetrics,
    cleanup: () => rtdbCache.cleanupExpiredEntries(),

    // Debug info
    size: () => rtdbCache.localCache.size,
    subscribers: () => rtdbCache.subscribers.size,
    connected: () => rtdbCache.connectionStatus,

    // Utility
    entries: () => Array.from(rtdbCache.localCache.keys()),
    dump: () => {
      const entries = [];
      rtdbCache.localCache.forEach((value, key) => {
        entries.push({
          key,
          expired: isCacheExpired(value),
          ttl: value.ttl,
          dataSize: JSON.stringify(value.data).length
        });
      });
      return entries;
    }
  };

  console.log('🚀 RTDB Cache debugging available via window.RTDBCache');
  console.log('Commands: info(), health(), performance(), stats(), dump()');
}

export default rtdbCache;